import { siteApiSlice } from "../../../app/siteApiSlice";
import { SelectListItem } from "../../../components/input/SelectList";
import NamedEntity from "../../../interfaces/NamedEntity";
import { CourseStatus } from "../../courses/coursesApiSlice";

export enum ReportLevel {
	Course = "Course",
	School = "School",
	District = "District",
	State = "State",
}

export interface AdminReportsRowBase {
	id: number;
	name: string;
	teachers: string;
}

export interface AdminReportsResponse<T> {
	rows: T[];
	totals: T;
}

export interface ProctorCourseViewModel {
	courseId: number;
	courseName: string;
	courseStatus: CourseStatus;
}

export interface TopPerformersPerLevel {
	writing: TopPerformerInfo[];
	reviews: TopPerformerInfo[];
	lessons: TopPerformerInfo[];
	reportLevel: ReportLevel;
}

export interface TopPerformerInfo {
	title: string;
	winner: string;
	value: string;
	teachers: string;
}

export const adminUsageReportApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		getAdminReports: build.query<boolean, void>({
			query: () => `/AdminReports/AdminReports`,
			providesTags: ["CurrentSchool", "CurrentDistrict"],
		}),

		getDistrictOptionsForReports: build.query<
			SelectListItem[],
			{ level?: ReportLevel; districtId?: number }
		>({
			query: (arg) => {
				const { level, districtId } = arg;
				return {
					url: `/AdminReports/GetDistrictsForSpaReports`,
					params: { level, districtId },
				};
			},
		}),
		getSchoolOptionsForReports: build.query<
			SelectListItem[],
			{ level: ReportLevel; districtId?: number; schoolId?: number }
		>({
			query: (arg) => {
				const { level, districtId, schoolId } = arg;
				return {
					url: `/AdminReports/GetSchoolsForSpaReports`,
					params: { level, districtId, schoolId },
				};
			},
		}),
		getTeacherOptionsForSchoolForReports: build.query<NamedEntity[], number>({
			query: (schoolId) => {
				return {
					url: `/AdminReports/GetProctorsForSchoolForSpaReports`,
					params: { schoolId },
				};
			},
		}),
		getCourseOptionsForTeacherForReports: build.query<
			ProctorCourseViewModel[],
			{ proctorId: number; schoolId: number }
		>({
			query: ({ proctorId, schoolId }) => {
				return {
					url: `/User/ProctorCoursesAjaxHandler`,
					params: { proctorId, schoolId },
				};
			},
		}),
		getSchoolYearsForReports: build.query<SelectListItem[], number | undefined>({
			query: (schoolYear) => {
				return {
					url: `/AdminReports/GetSchoolYearsForSpaReports`,
					params: { schoolYear },
				};
			},
		}),
		getDraftTypesForReports: build.query<SelectListItem[], number | undefined>({
			query: (draftType) => {
				return {
					url: `/AdminReports/GetDraftTypesForSpaReports`,
					params: { draftType },
				};
			},
		}),
		getLessonCategoriesForReports: build.query<SelectListItem[], number | undefined>({
			query: (tutorialCategoryId) => {
				return {
					url: `/AdminReports/GetTutorialCategoriesForSpaReports`,
					params: { tutorialCategoryId },
				};
			},
		}),
		getTopPerformers: build.query<
			TopPerformersPerLevel[],
			{ maxReportLevel: ReportLevel; districtId?: number; schoolId?: number }
		>({
			query: (arg) => {
				const { maxReportLevel, districtId, schoolId } = arg;
				return {
					url: `/AdminReports/TopPerformers`,
					params: { maxReportLevel, districtId, schoolId },
				};
			},
		}),
	}),
});

export const {
	useGetAdminReportsQuery,
	useGetDistrictOptionsForReportsQuery,
	useLazyGetSchoolOptionsForReportsQuery,
	useLazyGetTeacherOptionsForSchoolForReportsQuery,
	useLazyGetCourseOptionsForTeacherForReportsQuery,
	useGetSchoolYearsForReportsQuery,
	useGetDraftTypesForReportsQuery,
	useGetLessonCategoriesForReportsQuery,
	useGetTopPerformersQuery,
} = adminUsageReportApiSlice;
